import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth, displayUser } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { ReducedMotionProvider } from './context/ReducedMotionContext'
import Home               from './pages/Home'
import Navbar             from './components/Navbar'
import Login              from './pages/Login'
import Signup             from './pages/Signup'
import StoryReader        from './pages/StoryReader'
import UserProfile        from './pages/UserProfile'
import WritePage          from './pages/WritePage'
import BrowsePage         from './pages/BrowsePage'
import PrivacyPolicyPage  from './pages/PrivacyPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import CookiePolicyPage   from './pages/CookiePolicyPage'
import MessagesPage       from './pages/MessagesPage'
import EditProfilePage   from './pages/EditProfilePage'
import CookieBanner       from './components/CookieBanner'
import QuizzesPage      from './pages/QuizzesPage'
import QuizCreatorPage  from './pages/QuizCreatorPage'
import QuizPlayerPage   from './pages/QuizPlayerPage'
import DmcaPage         from './pages/DmcaPage'
import FeedPage         from './pages/FeedPage'
import FandomsPage      from './pages/FandomsPage'
import CommunityPage    from './pages/CommunityPage'
import GroupPage        from './pages/GroupPage'
import SettingsPage     from './pages/SettingsPage'
import WritingTipsPage  from './pages/WritingTipsPage'
import AdminPage        from './pages/AdminPage'
import GuidelinesPage  from './pages/GuidelinesPage'
import ContestsPage    from './pages/ContestsPage'
import BlogPage             from './pages/BlogPage'
import ForgotPasswordPage  from './pages/ForgotPasswordPage'
import ResetPasswordPage   from './pages/ResetPasswordPage'
import EmailChangeConfirm  from './pages/EmailChangeConfirm'
import AboutPage       from './pages/AboutPage'
import DraftsPage      from './pages/DraftsPage'
import ActivityPage    from './pages/ActivityPage'
import RequestsPage    from './pages/RequestsPage'
import AccountRestoration from './pages/AccountRestoration'
import NotFoundPage    from './pages/NotFoundPage'
import TransactionHistoryPage from './pages/TransactionHistoryPage'
import AtCapacityPage  from './pages/AtCapacityPage'
import PasswordGate    from './components/PasswordGate'
import TosGate         from './components/TosGate'
import DobGate         from './components/DobGate'
import SuspensionGate  from './components/SuspensionGate'
import VerifyEmailGate from './components/VerifyEmailGate'
import BottomNav       from './components/BottomNav'
import MusicNoteAnimation from './components/MusicNoteAnimation'
import { ToastProvider } from './context/ToastContext'
import { OnlineStatusProvider } from './context/OnlineStatusContext'
import { UnreadMessagesProvider } from './context/UnreadMessagesContext'
import ErrorBoundary   from './components/ErrorBoundary'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (!loading && !user) return <Navigate to="/login?reason=login_required" replace state={{ from: location.pathname }} />
  return children
}

function OwnProfileRedirect() {
  const { user, profile, loading } = useAuth()
  if (loading) return <div className="route-loading">Loading…</div>
  if (!user)   return <Navigate to="/login?reason=login_required" replace state={{ from: '/profile' }} />
  const username = displayUser(user, profile)?.username
  return <Navigate to={`/user/${username}`} replace />
}

function AppRoutes() {
  return (
    <>
      <main id="main-content">
      <Routes>
        <Route path="/"                element={<Home />} />
        <Route path="/login"           element={<Login />} />
        <Route path="/signup"          element={<Signup />} />
        <Route path="/account/restore" element={<AccountRestoration />} />
        <Route path="/story/:id"       element={<StoryReader />} />
        <Route path="/user/:username"  element={<UserProfile />} />
        <Route path="/profile"         element={<OwnProfileRedirect />} />
        <Route path="/browse"          element={<BrowsePage />} />
        <Route path="/fandoms"         element={<FandomsPage />} />
        <Route path="/community"       element={<CommunityPage />} />
        <Route path="/community/:id"   element={<GroupPage />} />
        <Route path="/privacy"         element={<PrivacyPolicyPage />} />
        <Route path="/terms"           element={<TermsOfServicePage />} />
        <Route path="/cookie-policy"   element={<CookiePolicyPage />} />
        <Route path="/dmca"            element={<DmcaPage />} />
        <Route path="/guidelines"      element={<GuidelinesPage />} />
        <Route path="/contests"        element={<ContestsPage />} />
        <Route path="/blog"            element={<BlogPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />
        <Route path="/auth/confirm"    element={<EmailChangeConfirm />} />
        <Route path="/about"           element={<AboutPage />} />
        <Route path="/feed"            element={<FeedPage />} />
        <Route path="/activity"        element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
        <Route path="/requests"        element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
        <Route path="/privacy-settings" element={<Navigate to="/settings?tab=Privacy" replace />} />
        <Route path="/drafts"          element={
          <ProtectedRoute><DraftsPage /></ProtectedRoute>
        } />
        <Route path="/write"           element={
          <ProtectedRoute><WritePage /></ProtectedRoute>
        } />
        <Route path="/story/:storyId/edit" element={
          <ProtectedRoute><WritePage /></ProtectedRoute>
        } />
        <Route path="/messages"        element={
          <ProtectedRoute><MessagesPage /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><SettingsPage /></ProtectedRoute>
        } />
        <Route path="/settings/profile" element={
          <ProtectedRoute><EditProfilePage /></ProtectedRoute>
        } />
        <Route path="/quizzes"        element={<QuizzesPage />} />
        <Route path="/quiz/create"    element={
          <ProtectedRoute><QuizCreatorPage /></ProtectedRoute>
        } />
        <Route path="/quiz/edit/:quizId" element={
          <ProtectedRoute><QuizCreatorPage /></ProtectedRoute>
        } />
        <Route path="/quiz/:id"       element={<QuizPlayerPage />} />
        <Route path="/tips"           element={<WritingTipsPage />} />
        <Route path="/admin"          element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
        <Route path="/at-capacity"     element={<AtCapacityPage />} />
        <Route path="*"               element={<NotFoundPage />} />
      </Routes>
      </main>
      <BottomNav />
      <CookieBanner />
    </>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function AutoRefresh() {
  useEffect(() => {
    // Page restored from browser back-forward cache (stale state)
    function onPageShow(e) { if (e.persisted) window.location.reload() }
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])
  return null
}

function GlobalSignInAnimation() {
  const { showSignInAnimation, dismissSignInAnimation } = useAuth()
  return showSignInAnimation ? <MusicNoteAnimation onDone={dismissSignInAnimation} /> : null
}

export default function App() {
  return (
    <ErrorBoundary>
    <PasswordGate>
      <ReducedMotionProvider>
      <ThemeProvider>
        <ToastProvider>
        <AuthProvider>
        <GlobalSignInAnimation />
        <OnlineStatusProvider>
          <UnreadMessagesProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AutoRefresh />
            <VerifyEmailGate>
              <TosGate>
                <DobGate>
                  <SuspensionGate>
                    <AppRoutes />
                  </SuspensionGate>
                </DobGate>
              </TosGate>
            </VerifyEmailGate>
          </BrowserRouter>
          </UnreadMessagesProvider>
        </OnlineStatusProvider>
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
      </ReducedMotionProvider>
    </PasswordGate>
    </ErrorBoundary>
  )
}
